import { useEffect, useRef } from "react";
import * as THREE from "three";

export function GridAnimation() {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    try {


      const container = containerRef.current;

      // Scene setup
      const scene = new THREE.Scene();

      // Add fog for fade-out effect at edges
      // Using the background color (#FAFAF9 = rgb(250, 250, 249))
      const fogColor = new THREE.Color(0xfafaf9);
      scene.fog = new THREE.Fog(fogColor, 15, 45);

      const camera = new THREE.PerspectiveCamera(
        60,
        container.clientWidth / container.clientHeight,
        0.1,
        1000
      );
      camera.position.set(0, 10, 15);
      camera.lookAt(0, 0, 0);

      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false,
      });
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.setClearColor(fogColor, 1);
      container.appendChild(renderer.domElement);

      // Create extended grid geometry
      const gridSize = 80;
      const gridDivisions = 80;
      const geometry = new THREE.PlaneGeometry(
        gridSize,
        gridSize,
        gridDivisions,
        gridDivisions
      );
      geometry.rotateX(-Math.PI / 2);

      // Store original positions
      const positions = geometry.attributes.position;
      if (!positions) return;
      const originalPositions = new Float32Array(positions.array);

      // Grid material with sage green color - using MeshBasicMaterial for fog support
      const material = new THREE.MeshBasicMaterial({
        color: 0x8b9a7d,
        wireframe: true,
        transparent: true,
        opacity: 0.4,
        fog: true,
      });

      const grid = new THREE.Mesh(geometry, material);
      scene.add(grid);

      // Mouse tracking for interaction
      const mouse = { x: 0, y: 0 };
      const handleMouseMove = (event: MouseEvent) => {
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
      };

      // Animation
      let time = 0;
      const animate = () => {
        time += 0.008;

        // Update grid vertices with wave animation
        for (let i = 0; i < positions.count; i++) {
          const x = originalPositions[i * 3]!;
          const z = originalPositions[i * 3 + 2]!;

          // Create wave effect
          const waveX = Math.sin(x * 0.2 + time) * 0.6;
          const waveZ = Math.cos(z * 0.2 + time * 0.8) * 0.6;

          // Add mouse influence
          const dx = x / (gridSize / 2) - mouse.x * 0.5;
          const dz = z / (gridSize / 2) - mouse.y * 0.5;
          const distance = Math.sqrt(dx * dx + dz * dz);
          const mouseInfluence = Math.max(0, 1 - distance) * Math.sin(time * 2) * 0.8;

          positions.array[i * 3 + 1] = waveX + waveZ + mouseInfluence;
        }
        positions.needsUpdate = true;

        // Subtle camera movement
        camera.position.x = Math.sin(time * 0.15) * 0.8;
        camera.position.z = 15 + Math.cos(time * 0.1) * 0.5;
        camera.lookAt(0, 0, 0);

        renderer.render(scene, camera);
        animationRef.current = requestAnimationFrame(animate);
      };

      animate();

      // Handle resize
      const handleResize = () => {
        if (!container) return;
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
      };
      window.addEventListener("resize", handleResize);
      window.addEventListener("mousemove", handleMouseMove);

      // Cleanup
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("resize", handleResize);
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
        geometry.dispose();
        material.dispose();
        renderer.dispose();
        if (container.contains(renderer.domElement)) {
          container.removeChild(renderer.domElement);
        }
      };

    } catch {

    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-0"
      aria-hidden="true"
    />
  );
}
